import { observer } from "mobx-react";
import { CountrySearchBox } from "Views/Components/CountrySearchBox/CountrySearchBox";
import { NumberTextField } from "Views/Components/NumberTextBox/NumberTextBox";
import { TextFieldSetter } from "Views/Components/TextBox/TextFieldSetter";
import ToggleSwitch from "Views/Components/ToggleSwitch/ToggleSwitch";
import { useAppSettingStore } from "../SettingStores/AppSettingPageStore";
import { TextArea } from "Views/Components/TextArea/TextArea";
import { useValidation } from "Hooks/useValidation";
import { Tooltip } from "Views/Components/Tooltip/Tooltip";
import { availableCountries } from "Models/Enums/CountryCodeEnum";

export const CreditProviderProductForm = observer(() => {
	const { creditProviderProductStore } = useAppSettingStore();
	const { selectedProviderProduct } = creditProviderProductStore;

	const validation = useValidation({
		"name": {
			value: selectedProviderProduct?.name,
			isRequired: true,
			requiredText: "Product name is required",
		},
		"productCode": {
			value: selectedProviderProduct?.productCode,
			isRequired: true,
			requiredText: "Product code is required",
		},
		"baseCost": {
			value: selectedProviderProduct?.baseCost,
			isRequired: true,
			requiredText: "Base cost is required",
			validate: value => {
				value.assertNumberBetween(0, 1000000, "Base cost must be between 0 and 1,000,000");
			},
		}
	});

	if (!selectedProviderProduct) {
		return <p className="alert alert__warning">No provider product detail available. Please try again later.</p>
	}

	return (
		<div className="section-form">
			<TextFieldSetter
				value={selectedProviderProduct.name}
				label="Product Name"
				setValue={value => creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, name: value })}
				errors={validation.get("name")}
			/>
			<TextFieldSetter
				value={selectedProviderProduct.productCode}
				label="Product Code"
				setValue={value => creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, productCode: value })}
				tooltip="The product code is used to identify the product. Changing the product code will create a new product."
				errors={validation.get("productCode")}
			/>
			<NumberTextField
				className="currency-text-field"
				model={selectedProviderProduct}
				modelProperty="baseCost"
				label="Base Cost"
				tooltip="The base cost is the cost of the product before any additional fees are applied in Australian dollars (AUD)."
				onAfterChange={event => {
					const value = parseFloat(event.target.value);
					creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, baseCost: value });
				}}
				errors={validation.get("baseCost")}
			/>
			<TextArea
				label="Description"
				model={selectedProviderProduct}
				modelProperty="description"
				onAfterChange={event => {
					creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, description: event.target.value });
				}}
				errors={validation.get("description")}
			/>
			<CountrySearchBox
				value={availableCountries.find(country => country.code === selectedProviderProduct.countryCode)}
				updateValue={value => {
					creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, countryCode: value.code });
				}}
			/>
			<TextFieldSetter
				value={selectedProviderProduct.s3FileKey}
				label="File Key"
				setValue={value => creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, s3FileKey: value })}
			/>
			<label id="isActive" htmlFor="isActive">State</label>
			<ToggleSwitch
				model={selectedProviderProduct}
				modelProperty="isActive"
				label="Active/Inactive"
				onAfterChecked={(_, checked) => {
					creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, isActive: checked });
				}}
			/>
			<br />

			<div>
				<label id="activeOnCreate" htmlFor="activeOnCreate">
					Active On Create
					<Tooltip id="activeOnCreate-tooltip"
						place="left"
						content={"When a business is created, they are automatically linked to the organisations' provider product in an inactive state. This create the link in an active state."} />

				</label>


				<ToggleSwitch
					model={selectedProviderProduct}
					modelProperty="activeOnCreate"
					label=""
					onAfterChecked={(_, checked) => {
						creditProviderProductStore.updateProviderProduct({ ...selectedProviderProduct, activeOnCreate: checked });
					}}
				/>
			</div>


		</div>
	)
});